import React, { useEffect, useRef, useState, useCallback } from 'react';
import { VncScreen } from 'react-vnc';
import WindowPortal from './WindowPortal';
import LoadingScreen from './LoadingScreen';
import DeviceOfflineOverlay from './DeviceOfflineOverlay';
import useHostReachability from '../utils/useHostReachability';

// Staged auto-retry watchdog, up to MAX_ATTEMPTS remounts of VncScreen
// (fresh websocket → fresh backend tunnel → fresh device session), spaced so
// we never turn into a connection storm:
//  - RETRY_AFTER_MS applies while the RFB handshake hasn't completed — a
//    session stuck that early (ARD credential hang, wedged x11vnc) never
//    recovers on its own, so remount fast.
//  - After the handshake, patience is PROGRESS-AWARE: Macs send the first
//    full-screen update as ONE big message (60KB idle … 4MB+ busy) that
//    noVNC can't paint until it fully arrives — 2s to 25s+ over a relayed
//    Tailscale path. Killing the session mid-transfer restarts the frame
//    from byte 0 and loops forever, so while bytes keep arriving we wait
//    (and show progress); we only remount once the stream actually stalls.
//    Byte counts come from a listener on noVNC's raw websocket
//    (rfb._sock._websocket — internal but stable; noVNC assigns onmessage
//    as a property, so addEventListener coexists). If that internal moves,
//    we fall back to a fixed post-handshake timeout.
const RETRY_AFTER_MS = 5000;
const FIRST_FRAME_STALL_MS = 10000; // no bytes for this long → remount
const FIRST_FRAME_HARD_CAP_MS = 120000; // bytes flow but nothing paints → remount
const FIRST_FRAME_FALLBACK_TIMEOUT_MS = 30000; // no byte visibility → old fixed wait
const MAX_ATTEMPTS = 6; // 1 initial + 5 automatic re-triggers

// Success means "a frame actually painted", not "RFB handshake completed".
// Macs (Apple Remote Desktop auth) answer the handshake in ~0.2s and can
// still never deliver a framebuffer — treating `connect` as success used to
// disarm the watchdog and strand the user on a dead session with no retry.
const PAINT_POLL_MS = 200;

// Field-proven fix for a stuck session: a SECOND connection reaching the
// device while the first is still open brings the first one alive (this is
// why clicking "VNC Desktop" a second time always worked). The remount
// watchdog is sequential (close, then reopen) and never replicates that, so
// for Macs we proactively open a throwaway concurrent websocket when the
// first attempt looks stuck. Budget: primary + nudge = 2 of the backend's 3
// per-target tunnel slots, held for ~NUDGE_HOLD_MS only.
const NUDGE_AFTER_MS = 2000;
const NUDGE_HOLD_MS = 1500;

// Delay before the manual "Reconnect now" button appears on the first
// attempt, so it doesn't flash during normal fast connects.
const RETRY_HINT_AFTER_MS = 1500;

const MODIFIER_KEYUPS = [
  [0xffe3, 'ControlLeft'], [0xffe4, 'ControlRight'],
  [0xffe1, 'ShiftLeft'], [0xffe2, 'ShiftRight'],
  [0xffe9, 'AltLeft'], [0xffea, 'AltRight'],
  [0xffe7, 'MetaLeft'], [0xffe8, 'MetaRight'],
  [0xffeb, 'OSLeft'], [0xffec, 'OSRight'],
  [0xfe03, 'AltRight'], // ISO_Level3_Shift (AltGr)
];

const VNCProxy = ({ host, onClose, title = 'VNC Proxy', macMini, vncUsername, vncPassword, deviceOnline = true, deviceName }) => {
  const screenRef = useRef(null);
  const shellRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [sessionKey, setSessionKey] = useState(0);
  const [gaveUp, setGaveUp] = useState(false);
  const [attempt, setAttempt] = useState(1);
  const [handshaked, setHandshaked] = useState(false);
  const [retryHintVisible, setRetryHintVisible] = useState(false);
  const [rxBytes, setRxBytes] = useState(0);
  const attemptRef = useRef(1);
  const attemptStartRef = useRef(performance.now());
  const paintedRef = useRef(false);
  const paintPollRef = useRef(null);
  const secFailRef = useRef(0);
  const rxBytesRef = useRef(0);
  const rxTrackedRef = useRef(false);
  const rfbRef = useRef(null);

  const captureRfb = useCallback(() => {
    const rfb = screenRef.current?.rfb;
    if (rfb) rfbRef.current = rfb;
    return rfbRef.current;
  }, []);

  const vncUrl = macMini
    ? `/api/vnc-auth?host=${host}&port=5900`
    : `/api/vnc?host=${host}&port=5900`;

  // Probe the device only while something is wrong (no picture yet, gave up,
  // or the polled status says offline). The live TCP dial outranks the polled
  // Tailscale status, which can lag a drop — in either direction — by
  // minutes: probe says reachable → trust it and keep the session; probe says
  // unreachable → the device is gone, whatever the poll thinks.
  const probeReachable = useHostReachability(host, loading || gaveUp || !deviceOnline);
  const deviceLost =
    probeReachable === false || (!deviceOnline && probeReachable !== true);

  const vlog = useCallback((...args) => {
    const ms = Math.round(performance.now() - attemptStartRef.current);
    console.log(`[vnc ${host} a${attemptRef.current} +${ms}ms]`, ...args);
  }, [host]);

  const forceVisibleCursor = useCallback(() => {
    const root = shellRef.current;
    if (!root) return;

    const canvas = root.querySelector('canvas');
    if (canvas) {
      canvas.style.cursor = 'default';
    }
    root.style.cursor = 'default';

    const rfb = screenRef.current?.rfb;
    if (rfb) {
      try {
        rfb.showDotCursor = true;
      } catch {}
    }
  }, []);

  // "Did a real frame paint yet?" — noVNC keeps the visible canvas fully
  // transparent (alpha 0) until the first decoded rect lands: resize-time
  // display.flip() calls have empty damage bounds and draw nothing, and every
  // decoder writes opaque pixels (alpha 255). So any nonzero alpha byte means
  // a genuine framebuffer update painted — even an all-black Mac screen is
  // opaque black. (noVNC dispatches no event for framebuffer updates, so the
  // canvas itself is the only public signal.)
  const isCanvasPainted = useCallback(() => {
    const canvas = shellRef.current?.querySelector('canvas');
    if (!canvas || !canvas.width || !canvas.height) return false;
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    for (const [fx, fy] of [[0.5, 0.5], [0.25, 0.25], [0.75, 0.75]]) {
      const d = ctx.getImageData(
        Math.floor(canvas.width * fx), Math.floor(canvas.height * fy), 8, 8).data;
      for (let i = 3; i < d.length; i += 4) {
        if (d[i] !== 0) return true;
      }
    }
    return false;
  }, []);

  const stopPaintPoll = useCallback(() => {
    if (paintPollRef.current) {
      clearInterval(paintPollRef.current);
      paintPollRef.current = null;
    }
  }, []);

  const staleModsClearedRef = useRef(false);
  const clearStaleModifiers = useCallback(() => {
    if (staleModsClearedRef.current) return true;
    const rfb = captureRfb();
    if (!rfb) return false;
    try {
      MODIFIER_KEYUPS.forEach(([keysym, code]) => rfb.sendKey(keysym, code, false));
      staleModsClearedRef.current = true;
      vlog('sent stale-modifier keyups');
      return true;
    } catch {
      return false;
    }
  }, [captureRfb, vlog]);

  // Attach a byte counter to noVNC's raw websocket. react-vnc publishes
  // `rfb` on its ref handle asynchronously, so this often loses the race
  // when called right at onConnect (measured: ~80% of the time in Firefox)
  // — callers retry it from the watchdog tick until it lands.
  const tryAttachByteCounter = useCallback(() => {
    captureRfb();
    clearStaleModifiers();
    if (rxTrackedRef.current) return true;
    const ws = screenRef.current?.rfb?._sock?._websocket;
    if (!ws || typeof ws.addEventListener !== 'function') return false;
    rxTrackedRef.current = true;
    ws.addEventListener('message', (ev) => {
      rxBytesRef.current +=
        ev.data?.byteLength ?? (typeof ev.data === 'string' ? ev.data.length : 0);
    });
    vlog('byte-progress counter attached');
    return true;
  }, [vlog, captureRfb, clearStaleModifiers]);

  const startPaintPoll = useCallback(() => {
    stopPaintPoll();
    paintPollRef.current = setInterval(() => {
      if (!isCanvasPainted()) return;
      stopPaintPoll();
      paintedRef.current = true;
      captureRfb();
      clearStaleModifiers();
      secFailRef.current = 0;
      attemptRef.current = 1;
      vlog(`first frame painted (rx=${rxBytesRef.current}B)`);
      setAttempt(1);
      setLoading(false);
    }, PAINT_POLL_MS);
  }, [isCanvasPainted, stopPaintPoll, vlog, captureRfb, clearStaleModifiers]);

  const releaseAllInput = useCallback(() => {
    const rfb = captureRfb();
    if (!rfb) return;
    try { rfb._keyboard?._allKeysUp?.(); } catch { /* not connected */ }
    try {
      if (rfb._rfbConnectionState === 'connected') {
        const pos = rfb._mousePos ?? {};
        rfb._sendMouse(pos.x ?? 0, pos.y ?? 0, 0);
      }
    } catch { /* not connected */ }
  }, [captureRfb]);

  // Remount VncScreen: closes the current (stuck) session and opens a fresh
  // one — the programmatic equivalent of clicking "VNC Desktop" again.
  const resetSession = useCallback(() => {
    releaseAllInput();
    setSessionKey((k) => k + 1);
  }, [releaseAllInput]);

  const triggerRetry = useCallback(() => {
    if (attemptRef.current >= MAX_ATTEMPTS) {
      vlog(`giving up after ${MAX_ATTEMPTS} attempts`);
      setGaveUp(true);
      return;
    }
    vlog('no picture — remounting for a fresh session');
    attemptRef.current += 1;
    setAttempt(attemptRef.current);
    setLoading(true);
    resetSession();
  }, [resetSession, vlog]);

  const retryNow = useCallback(() => {
    attemptRef.current = 1;
    secFailRef.current = 0;
    setAttempt(1);
    setGaveUp(false);
    setErr('');
    setLoading(true);
    resetSession();
  }, [resetSession]);

  // Per-attempt bookkeeping: each remount (sessionKey bump) is a new attempt.
  useEffect(() => {
    attemptStartRef.current = performance.now();
    paintedRef.current = false;
    rxBytesRef.current = 0;
    rxTrackedRef.current = false;
    rfbRef.current = null;
    staleModsClearedRef.current = false;
    setRxBytes(0);
    setHandshaked(false);
    return () => stopPaintPoll();
  }, [sessionKey, stopPaintPoll]);

  // The watchdog: while a session has no picture yet, decide when to remount
  // for a fresh connection. Clears the moment a frame paints (loading ->
  // false) or we give up. Pre-handshake: fixed short fuse. Post-handshake:
  // patient while download progress continues (big first frame streaming),
  // remount only when the byte stream stalls; the 2s nudge is the fast
  // rescue for a session that connects but never sends.
  useEffect(() => {
    if (!loading || gaveUp || deviceLost) return undefined;
    if (!handshaked) {
      const t = setTimeout(triggerRetry, RETRY_AFTER_MS);
      return () => clearTimeout(t);
    }
    const started = performance.now();
    let lastBytes = rxBytesRef.current;
    let lastProgressAt = performance.now();
    const iv = setInterval(() => {
      // Keep trying to hook the byte counter — react-vnc publishes `rfb`
      // asynchronously and onConnect usually loses that race (esp. Firefox).
      tryAttachByteCounter();
      const now = performance.now();
      const bytes = rxBytesRef.current;
      if (bytes > lastBytes) {
        lastBytes = bytes;
        lastProgressAt = now;
        setRxBytes(bytes);
      }
      const stalledMs = now - lastProgressAt;
      // With byte visibility: remount only when the stream truly stalls.
      // Without it (counter never attached): fall back to a fixed window.
      const limit = rxTrackedRef.current
        ? FIRST_FRAME_STALL_MS
        : FIRST_FRAME_FALLBACK_TIMEOUT_MS;
      if (stalledMs > limit || now - started > FIRST_FRAME_HARD_CAP_MS) {
        vlog(`no first frame (rx=${bytes}B, ${Math.round(stalledMs / 1000)}s without progress, tracked=${rxTrackedRef.current})`);
        clearInterval(iv);
        triggerRetry();
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [sessionKey, loading, gaveUp, handshaked, deviceLost, triggerRetry, tryAttachByteCounter, vlog]);

  // Device lost: the watchdog and nudge are paused above (retrying a dead
  // device is just a connection storm for x11vnc to untangle later) and
  // DeviceOfflineOverlay explains the frozen frame. When the device comes
  // back, start over with a fresh session — unless a picture is live, which
  // means a stale "offline" verdict just got corrected by the probe.
  const wasOfflineRef = useRef(false);
  useEffect(() => {
    if (deviceLost) {
      wasOfflineRef.current = true;
      return;
    }
    if (wasOfflineRef.current) {
      wasOfflineRef.current = false;
      if (loading || gaveUp) {
        vlog('device reachable again — starting a fresh session');
        retryNow();
      }
    }
  }, [deviceLost, loading, gaveUp, retryNow, vlog]);

  // Mac-only concurrent nudge (see NUDGE_AFTER_MS comment): the backend
  // dials the device's VNC port as soon as the socket opens. Echo the
  // server's RFB version banner back so the connection enters screensharingd's
  // real protocol path (the field-proven cure was a full second VNC client —
  // a silent TCP touch has been observed NOT to unstick a wedged session).
  // Same-origin, so the JWT cookie rides along automatically.
  useEffect(() => {
    if (!macMini || gaveUp || deviceLost) return undefined;
    const t = setTimeout(() => {
      if (paintedRef.current) return;
      // Data already streaming = connection alive, just a big first frame;
      // nothing to unstick.
      if (rxBytesRef.current > 100000) return;
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      vlog('nudge: opening throwaway second connection');
      const nudge = new WebSocket(`${proto}://${window.location.host}${vncUrl}`);
      nudge.binaryType = 'arraybuffer';
      let sentVersion = false;
      const closeNudge = () => {
        try { nudge.close(); } catch {}
      };
      nudge.onmessage = (ev) => {
        // First message is the server's "RFB 003.xxx\n" banner; echoing it
        // completes version negotiation and the server proceeds to offer
        // security types.
        if (!sentVersion) {
          sentVersion = true;
          try { nudge.send(ev.data); } catch {}
        }
      };
      nudge.onopen = () => setTimeout(() => {
        vlog(`nudge: closing (version exchange ${sentVersion ? 'done' : 'not reached'})`);
        closeNudge();
      }, NUDGE_HOLD_MS);
      nudge.onerror = closeNudge;
    }, NUDGE_AFTER_MS);
    return () => clearTimeout(t);
  }, [sessionKey, macMini, gaveUp, deviceLost, vncUrl, vlog]);

  // Manual retry must always be reachable — on attempt 1 it fades in after a
  // short delay so it doesn't flash during normal fast connects.
  useEffect(() => {
    setRetryHintVisible(false);
    if (!loading || gaveUp) return undefined;
    const t = setTimeout(() => setRetryHintVisible(true), RETRY_HINT_AFTER_MS);
    return () => clearTimeout(t);
  }, [sessionKey, loading, gaveUp]);

  const handleCreds = useCallback(() => {
    shellRef.current?.focus?.();

    const username = vncUsername ?? 'tocaro';
    const password = vncPassword ?? 'Jibber4sh!';

    screenRef.current?.sendCredentials?.({ username, password });
  }, [vncUsername, vncPassword]);

  useEffect(() => {
    const el = shellRef.current;
    if (!el) return;

    const blockCtrlWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const blockZoomKeys = (e) => {
      if ((e.ctrlKey || e.metaKey) && ['+', '-', '=', '0'].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    el.addEventListener('wheel', blockCtrlWheel, { passive: false, capture: true });
    el.addEventListener('keydown', blockZoomKeys, { passive: false, capture: true });

    const popupWin = el.ownerDocument?.defaultView;
    const releaseOnLeave = () => releaseAllInput();
    let closedPoll = null;
    if (popupWin && popupWin !== window) {
      popupWin.addEventListener('blur', releaseOnLeave);
      popupWin.addEventListener('pagehide', releaseOnLeave);
      popupWin.addEventListener('beforeunload', releaseOnLeave);
      closedPoll = setInterval(() => {
        if (popupWin.closed) {
          clearInterval(closedPoll);
          closedPoll = null;
          releaseAllInput();
        }
      }, 250);
    }

    return () => {
      el.removeEventListener('wheel', blockCtrlWheel, { capture: true });
      el.removeEventListener('keydown', blockZoomKeys, { capture: true });
      if (closedPoll) clearInterval(closedPoll);
      if (popupWin && popupWin !== window) {
        try {
          popupWin.removeEventListener('blur', releaseOnLeave);
          popupWin.removeEventListener('pagehide', releaseOnLeave);
          popupWin.removeEventListener('beforeunload', releaseOnLeave);
        } catch { /* popup already closed */ }
      }
      releaseAllInput();
    };
  }, [releaseAllInput]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        forceVisibleCursor();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [forceVisibleCursor]);

  useEffect(() => {
    vlog(`session start: url=${vncUrl} macMini=${!!macMini}`);
  }, [sessionKey, vncUrl, macMini, vlog]);

  return (
    <WindowPortal title={title} onClose={onClose}>
      <style>{`
        .vnc-shell, .vnc-shell * { cursor: default; }
        .vnc-shell canvas { cursor: default !important; }
      `}</style>

      <div style={{ width: '100%', height: '100%', background: '#000', position: 'relative' }}>
        {deviceLost && (
          <DeviceOfflineOverlay deviceName={deviceName} onClose={onClose} />
        )}

        {gaveUp && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1rem',
              color: '#9ca3af',
              fontFamily: 'sans-serif',
            }}
          >
            <p style={{ margin: 0 }}>
              {err ? `VNC ${err}` : `Couldn't get a picture after ${MAX_ATTEMPTS} attempts.`}
            </p>
            <button
              onClick={retryNow}
              style={{
                background: '#1d4ed8',
                color: '#fff',
                border: '1px solid #1e40af',
                borderRadius: 4,
                padding: '6px 16px',
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
          </div>
        )}

        {!gaveUp && loading && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 2 }}>
            <LoadingScreen text={err ? `VNC error: ${err}` : undefined} />
            {(attempt > 1 || retryHintVisible) && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '15%',
                  left: 0,
                  right: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.75rem',
                }}
              >
                <span style={{ color: '#9ca3af', fontSize: 14, fontFamily: 'sans-serif' }}>
                  {handshaked
                    ? `Connected — receiving the first screen update…${
                        rxBytes > 0 ? ` (${(rxBytes / 1048576).toFixed(1)} MB)` : ''
                      }`
                    : attempt > 1
                      ? `Reconnecting to get a picture… (attempt ${attempt} of ${MAX_ATTEMPTS})`
                      : 'Taking too long?'}
                </span>
                <button
                  onClick={triggerRetry}
                  style={{
                    background: '#1d4ed8',
                    color: '#fff',
                    border: '1px solid #1e40af',
                    borderRadius: 6,
                    padding: '8px 18px',
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                >
                  Reconnect now
                </button>
              </div>
            )}
          </div>
        )}

        {!gaveUp && (
        <div
          ref={shellRef}
          className="vnc-shell"
          style={{ width: '100%', height: '100%', outline: 'none' }}
          tabIndex={0}
          onMouseDown={() => shellRef.current?.focus?.()}
        >
          <VncScreen
            key={sessionKey}
            url={vncUrl}
            scaleViewport
            background="#000"
            style={{ width: '100%', height: '100%' }}
            ref={screenRef}
            // Macs speak Apple Remote Desktop auth (RFB 003.889). Hand noVNC
            // the credentials up front so it completes the auth handshake
            // itself, instead of pausing on the async credentialsrequired
            // round-trip (where Mac sessions were hanging on "Connecting…").
            rfbOptions={
              macMini
                ? {
                    credentials: {
                      username: vncUsername ?? 'tocaro',
                      password: vncPassword ?? 'Jibber4sh!',
                    },
                  }
                : undefined
            }
            // Disable react-vnc's own retry timer; our watchdog (which
            // remounts and gets a fresh session) is the only reconnect path.
            retryDuration={3600000}
            onConnect={() => {
              // Handshake done ≠ picture. Keep `loading` (and therefore the
              // watchdog) armed; only the paint poll clears it.
              vlog('rfb handshake complete — waiting for first frame');
              setErr('');
              setHandshaked(true);
              // Count arriving bytes so the watchdog can tell "big first
              // frame still downloading" from "stream went dead". Often
              // fails here (rfb not yet on the ref) — the watchdog tick
              // keeps retrying the attach.
              tryAttachByteCounter();
              startPaintPoll();
              shellRef.current?.focus?.();

              setTimeout(() => {
                forceVisibleCursor();
                if (macMini) handleCreds();
              }, 50);
            }}
            onDisconnect={(e) => {
              // Failed/dropped connection: back to loading; the watchdog
              // handles the retry.
              vlog(`disconnected (clean=${e?.detail?.clean ?? 'unknown'})`);
              paintedRef.current = false;
              stopPaintPoll();
              setHandshaked(false); // dead socket: back to the fast 5s stage
              setLoading(true);
            }}
            onCredentialsRequired={macMini ? handleCreds : undefined}
            onSecurityFailure={(e) => {
              // Mac ARD auth can fail transiently — retry once before
              // surfacing the failure.
              vlog(`security failure (status=${e?.detail?.status} reason=${e?.detail?.reason})`);
              paintedRef.current = false;
              stopPaintPoll();
              setLoading(true);
              if (secFailRef.current < 1 && attemptRef.current < MAX_ATTEMPTS) {
                secFailRef.current += 1;
                triggerRetry();
              } else {
                setErr(`security failure${e?.detail?.reason ? `: ${e.detail.reason}` : ''}`);
                setGaveUp(true);
              }
            }}
          />
        </div>
        )}
      </div>
    </WindowPortal>
  );
};

export default VNCProxy;
